import React, { useState, useEffect } from 'react';
import ReactModal from 'react-modal';
import { ReactComponent as CloseModalIcon } from '../../assets/svg/CloseModalIcon.svg';
import '../../styles/modals/StudentInfoModal.css';
import { useForm } from 'react-hook-form';
import Loading from '../Loading';
import { notify } from '../../utils/util';
import { sendUpdateNotification, SendUpdateNotificationDto } from '../../server/server';

interface SendNotificationModalProps {
	modalIsOpen: boolean;
	closeModal: () => void;
}

interface FormData {
	channel: 'staging' | 'production' | 'all';
	updateType: 'APP_STORE_UPDATE' | 'OTA_UPDATE';
	title: string;
	body: string;
	minAppVersion: string;
}

interface SendResult {
	success: boolean;
	sentCount: number;
	failedCount: number;
	errors?: string[];
}

const DEFAULT_MESSAGES = {
	APP_STORE_UPDATE: {
		title: 'Update Available',
		body: 'A new version of Upstrive is available. Update now for the best experience!',
	},
	OTA_UPDATE: {
		title: 'New Update Ready',
		body: 'Open the app to get the latest features!',
	},
};

function SendNotificationModal({ modalIsOpen, closeModal }: SendNotificationModalProps) {
	const [processing, setProcessing] = useState(false);
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [result, setResult] = useState<SendResult | null>(null);

	const {
		register,
		handleSubmit,
		watch,
		reset,
		formState: { errors },
	} = useForm<FormData>({
		defaultValues: {
			channel: 'staging',
			updateType: 'APP_STORE_UPDATE',
			title: '',
			body: '',
			minAppVersion: '',
		},
	});

	const watchUpdateType = watch('updateType');
	const watchTitle = watch('title');
	const watchBody = watch('body');
	const watchChannel = watch('channel');
	const watchMinAppVersion = watch('minAppVersion');

	useEffect(() => {
		ReactModal.setAppElement('#root');
		if (modalIsOpen) {
			const element = document.getElementById('home');
			if (element) element.style.filter = 'blur(5px)';
		} else {
			const element = document.getElementById('home');
			if (element) element.style.filter = 'unset';
		}
	}, [modalIsOpen]);

	const getPreviewTitle = () => {
		return watchTitle?.trim() || DEFAULT_MESSAGES[watchUpdateType]?.title || '';
	};

	const getPreviewBody = () => {
		return watchBody?.trim() || DEFAULT_MESSAGES[watchUpdateType]?.body || '';
	};

	const validateSemver = (value: string) => {
		if (!value) return true;
		const semverRegex = /^\d+\.\d+\.\d+$/;
		return semverRegex.test(value) || 'Invalid version format (use X.X.X)';
	};

	const onSubmitForm = () => {
		setShowConfirmation(true);
	};

	const handleConfirmSend = async () => {
		setShowConfirmation(false);
		setProcessing(true);

		const data: SendUpdateNotificationDto = {
			channel: watchChannel,
			updateType: watchUpdateType,
		};

		if (watchTitle?.trim()) {
			data.title = watchTitle.trim();
		}
		if (watchBody?.trim()) {
			data.body = watchBody.trim();
		}
		if (watchMinAppVersion?.trim()) {
			data.minAppVersion = watchMinAppVersion.trim();
		}

		const resp = await sendUpdateNotification(data);

		if (resp.status === 200 || resp.status === 201) {
			setResult(resp.data);
			notify('Notification sent successfully!');
		} else {
			setResult({
				success: false,
				sentCount: 0,
				failedCount: 0,
				errors: [resp.data?.message || 'Failed to send notification'],
			});
			notify('Failed to send notification');
		}
		setProcessing(false);
	};

	const cleanUpFunction = () => {
		reset();
		setProcessing(false);
		setShowConfirmation(false);
		setResult(null);
	};

	const handleClose = () => {
		cleanUpFunction();
		closeModal();
	};

	const getChannelLabel = (channel: string) => {
		switch (channel) {
			case 'staging':
				return 'Staging';
			case 'production':
				return 'Production';
			case 'all':
				return 'All Users';
			default:
				return channel;
		}
	};

	const getUpdateTypeLabel = (type: string) => {
		switch (type) {
			case 'APP_STORE_UPDATE':
				return 'App Store Update';
			case 'OTA_UPDATE':
				return 'OTA Update';
			default:
				return type;
		}
	};

	return (
		<ReactModal
			isOpen={modalIsOpen}
			onAfterClose={cleanUpFunction}
			onRequestClose={handleClose}
			style={{
				overlay: {
					zIndex: 10,
					background: '#00000045',
				},
				content: {
					padding: 0,
					paddingTop: 30,
					paddingBottom: 30,
					opacity: 1,
					width: 700,
					margin: 'auto',
					backgroundColor: 'transparent',
					maxHeight: '100vh',
					border: 'none',
					boxShadow: 'none',
					overflow: 'auto',
				},
			}}
			className='scrollbar-display-none'
		>
			<div className='student-info-modal'>
				<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
					<div className='close-modal-icon' onClick={handleClose}>
						<CloseModalIcon />
					</div>
				</div>

				<div className='bg-white rounded-xl mx-3 flex-1 flex flex-col gap-5 px-[40px] py-[30px]'>
					<div className='flex flex-row items-center justify-between'>
						<p className='text-[24px] font-bold leading-8 text-darkViolet'>Send Update Notification</p>
					</div>

					{result ? (
						<div className='flex flex-col gap-6'>
							<div className={`p-6 rounded-lg ${result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
								<p className={`text-[18px] font-bold ${result.success ? 'text-green-700' : 'text-red-700'}`}>{result.success ? 'Notification Sent Successfully!' : 'Notification Failed'}</p>
								<div className='mt-4 flex flex-col gap-2'>
									<p className='text-[16px] text-primaryViolet'>
										<span className='font-semibold'>Sent:</span> {result.sentCount} users
									</p>
									<p className='text-[16px] text-primaryViolet'>
										<span className='font-semibold'>Failed:</span> {result.failedCount} users
									</p>
									{result.errors && result.errors.length > 0 && (
										<div className='mt-2'>
											<p className='text-[14px] font-semibold text-red-600'>Errors:</p>
											<ul className='list-disc list-inside mt-1'>
												{result.errors.map((error, index) => (
													<li key={index} className='text-[14px] text-red-600'>
														{error}
													</li>
												))}
											</ul>
										</div>
									)}
								</div>
							</div>
							<div className='flex flex-row justify-end gap-4'>
								<button type='button' onClick={handleClose} className='px-6 py-3 rounded-full border border-primaryViolet bg-primaryViolet text-[16px] font-semibold text-white transition hover:bg-opacity-90'>
									Close
								</button>
								<button
									type='button'
									onClick={() => setResult(null)}
									className='px-6 py-3 rounded-full border border-mudPurple bg-white text-[16px] font-semibold text-primaryViolet transition hover:bg-gray-50'
								>
									Send Another
								</button>
							</div>
						</div>
					) : showConfirmation ? (
						<div className='flex flex-col gap-6'>
							<div className='p-6 rounded-lg bg-yellow-50 border border-yellow-200'>
								<p className='text-[18px] font-bold text-yellow-700'>Confirm Send Notification</p>
								<p className='text-[14px] text-yellow-600 mt-2'>This will send a push notification to potentially many users. Please review the details below.</p>
							</div>

							<div className='flex flex-col gap-3 p-4 rounded-lg bg-gray-50'>
								<p className='text-[14px] text-primaryViolet'>
									<span className='font-semibold'>Channel:</span> {getChannelLabel(watchChannel)}
								</p>
								<p className='text-[14px] text-primaryViolet'>
									<span className='font-semibold'>Update Type:</span> {getUpdateTypeLabel(watchUpdateType)}
								</p>
								{watchMinAppVersion && (
									<p className='text-[14px] text-primaryViolet'>
										<span className='font-semibold'>Target Version:</span> Users with app version below {watchMinAppVersion}
									</p>
								)}
								<p className='text-[14px] text-primaryViolet'>
									<span className='font-semibold'>Title:</span> {getPreviewTitle()}
								</p>
								<p className='text-[14px] text-primaryViolet'>
									<span className='font-semibold'>Body:</span> {getPreviewBody()}
								</p>
							</div>

							<div className='flex flex-row justify-end gap-4'>
								<button
									type='button'
									onClick={() => setShowConfirmation(false)}
									className='px-6 py-3 rounded-full border border-mudPurple bg-white text-[16px] font-semibold text-primaryViolet transition hover:bg-gray-50'
								>
									Go Back
								</button>
								<button
									type='button'
									onClick={handleConfirmSend}
									className='px-6 py-3 rounded-full border border-primaryViolet bg-primaryViolet text-[16px] font-semibold text-white transition hover:bg-opacity-90'
								>
									Confirm & Send
								</button>
							</div>
						</div>
					) : (
						<form onSubmit={handleSubmit(onSubmitForm)} className='flex flex-col gap-6'>
							<div className='flex flex-col gap-2'>
								<label className='text-[16px] font-semibold text-primaryViolet'>
									Channel <span className='text-red-500'>*</span>
								</label>
								<select
									{...register('channel', { required: true })}
									className='rounded-lg border border-mudPurple px-4 py-3 text-[16px] font-medium text-primaryViolet outline-none focus:border-primaryViolet'
								>
									<option value='staging'>Staging</option>
									<option value='production'>Production</option>
									<option value='all'>All Users</option>
								</select>
								{errors.channel && <span className='text-[14px] text-red-500'>Channel is required</span>}
							</div>

							<div className='flex flex-col gap-2'>
								<label className='text-[16px] font-semibold text-primaryViolet'>
									Update Type <span className='text-red-500'>*</span>
								</label>
								<div className='flex flex-row gap-6'>
									<label className='flex items-center gap-2 cursor-pointer'>
										<input type='radio' value='APP_STORE_UPDATE' {...register('updateType', { required: true })} className='h-5 w-5' />
										<span className='text-[16px] font-medium text-primaryViolet'>App Store Update</span>
									</label>
									<label className='flex items-center gap-2 cursor-pointer'>
										<input type='radio' value='OTA_UPDATE' {...register('updateType', { required: true })} className='h-5 w-5' />
										<span className='text-[16px] font-medium text-primaryViolet'>OTA Update</span>
									</label>
								</div>
								{errors.updateType && <span className='text-[14px] text-red-500'>Update type is required</span>}
							</div>

							<div className='flex flex-col gap-2'>
								<label className='text-[16px] font-semibold text-primaryViolet'>Custom Title (optional)</label>
								<input
									type='text'
									{...register('title')}
									className='rounded-lg border border-mudPurple px-4 py-3 text-[16px] font-medium text-primaryViolet outline-none focus:border-primaryViolet'
									placeholder='Leave empty to use default title'
								/>
							</div>

							<div className='flex flex-col gap-2'>
								<label className='text-[16px] font-semibold text-primaryViolet'>Custom Body (optional)</label>
								<textarea
									{...register('body')}
									className='rounded-lg border border-mudPurple px-4 py-3 text-[16px] font-medium text-primaryViolet outline-none focus:border-primaryViolet'
									placeholder='Leave empty to use default message'
									rows={3}
								/>
							</div>

							<div className='flex flex-col gap-2'>
								<label className='text-[16px] font-semibold text-primaryViolet'>Minimum App Version (optional)</label>
								<input
									type='text'
									{...register('minAppVersion', { validate: validateSemver })}
									className='rounded-lg border border-mudPurple px-4 py-3 text-[16px] font-medium text-primaryViolet outline-none focus:border-primaryViolet'
									placeholder='e.g., 1.2.0'
								/>
								<p className='text-[12px] text-mudPurple'>Only users with app version BELOW this value will receive the notification</p>
								{errors.minAppVersion && <span className='text-[14px] text-red-500'>{errors.minAppVersion.message}</span>}
							</div>

							<div className='flex flex-col gap-2 p-4 rounded-lg bg-gray-50'>
								<p className='text-[14px] font-semibold text-primaryViolet'>Preview</p>
								<div className='p-3 rounded-lg bg-white border border-gray-200'>
									<p className='text-[16px] font-bold text-primaryViolet'>{getPreviewTitle()}</p>
									<p className='text-[14px] text-mudPurple mt-1'>{getPreviewBody()}</p>
								</div>
							</div>

							<div className='flex flex-row justify-end gap-4 pt-4 border-t'>
								<button
									type='button'
									onClick={handleClose}
									className='px-6 py-3 rounded-full border border-mudPurple bg-white text-[16px] font-semibold text-primaryViolet transition hover:bg-gray-50'
									disabled={processing}
								>
									Cancel
								</button>
								<button
									type='submit'
									className='flex items-center gap-2 px-6 py-3 rounded-full border border-primaryViolet bg-primaryViolet text-[16px] font-semibold text-white transition hover:bg-opacity-90 disabled:opacity-50'
									disabled={processing}
								>
									{processing ? <Loading size={20} primaryColor='#fff' stroke={2} /> : 'Send Notification'}
								</button>
							</div>
						</form>
					)}
				</div>
			</div>
		</ReactModal>
	);
}

export default SendNotificationModal;
